import { Box, Flex, Text } from "@chakra-ui/react";

import { DifferenceBadge } from "../components/DifferenceBadge";
import { useMemo } from "react";

export const energyValueDescriptors = {
  co2Emission: {
    color: "brand.skyblue.100",
    title: (
      <p>
        CO<sub>2</sub>
      </p>
    ),
    unit: "kg/jaar",
  },
  electricityConsumption: {
    color: "brand.royalblue.100",
    title: "Stroom",
    unit: "kWh/jaar",
  },
  gasConsumption: {
    color: "brand.darkblue.100",
    title: "Gas",
    unit: (
      <p>
        m<sup>3</sup>/jaar
      </p>
    ),
  },
  heatNetworkConsumption: {
    color: "brand.orange.100",
    title: "Warmtenet",
    unit: "J/jaar",
  },
} as const;

function Bar({
  target,
  value,
  color,
}: {
  target: number;
  value: number;
  color: string;
}) {
  return (
    <Box bg="brand.gray.100" borderRadius={2} flex={1} h={1} overflow="hidden">
      <Box
        bg={color}
        h="100%"
        w={`${Math.min(
          100,
          Math.max(
            0,
            ((value - (target - 20)) / (target - (target - 20))) * 100,
          ),
        )}%`}
      />
    </Box>
  );
}

const targets = [20, 40, 60, 80, 100];

interface Props {
  dataKey: keyof typeof energyValueDescriptors;
  baseValue: number;
  currentValue?: number;
  invert?: boolean;
  info?: React.ReactNode;
}

export function EnergyDisplay({
  dataKey,
  baseValue,
  currentValue,
  invert = false,
  info = null,
}: Props) {
  const { title, unit, color } = energyValueDescriptors[dataKey];

  const displayValue = useMemo(
    () =>
      (currentValue ?? baseValue).toLocaleString(
        document.documentElement.lang,
        { minimumFractionDigits: 0, maximumFractionDigits: 2 },
      ),
    [baseValue, currentValue],
  );

  return (
    <Box my={2}>
      {typeof currentValue === "undefined" && (
        <Flex gap={2} alignItems="center">
          <Text>{title}</Text>
          <Text color="brand.gray.120" fontSize={14} lineHeight="24px">
            {unit}
          </Text>
          <Text ml="auto">{displayValue}</Text>
          {info}
        </Flex>
      )}
      {typeof currentValue !== "undefined" && (
        <Flex mx="auto" w="max-content" placeItems="center" gap={2}>
          <Text>{displayValue}</Text>
          <DifferenceBadge
            compareValue={currentValue}
            currentValue={baseValue}
            invert={invert}
          />
          {info}
        </Flex>
      )}
      <Flex gap={1} mt={4}>
        {targets.map((t) => (
          <Bar
            key={t}
            color={color}
            target={t}
            value={
              typeof currentValue === "number"
                ? (currentValue / baseValue) * 100
                : 100
            }
          />
        ))}
      </Flex>
    </Box>
  );
}
